import type { ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import styles from './RecordDetailShell.module.css';

export type RecordDetailTab = { id: string; label: string };

type ShellProps = {
  ariaLabel: string;
  children: ReactNode;
  className?: string;
};

export function RecordDetailShell({ ariaLabel, children, className }: ShellProps) {
  return (
    <aside
      className={[styles.shell, className].filter(Boolean).join(' ')}
      aria-label={ariaLabel}
    >
      {children}
    </aside>
  );
}

type HeaderProps = {
  identity?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
};

RecordDetailShell.Header = function RecordDetailShellHeader({
  identity,
  title,
  subtitle,
  badges,
  onClose,
  closeLabel = 'Paneel sluiten',
}: HeaderProps) {
  return (
    <header className={styles.header}>
      <div className={styles.identity}>
        {identity}
        <div className={styles.identityText}>
          {typeof title === 'string' ? <h2 className={styles.title}>{title}</h2> : title}
          {subtitle ? (
            typeof subtitle === 'string' ? (
              <p className={styles.subtitle}>{subtitle}</p>
            ) : (
              subtitle
            )
          ) : null}
          {badges ? <div className={styles.badges}>{badges}</div> : null}
        </div>
      </div>
      {onClose ? (
        <Button
          variant="ghost"
          onClick={onClose}
          aria-label={closeLabel}
          className={styles.closeBtn}
        >
          ✕
        </Button>
      ) : null}
    </header>
  );
};

RecordDetailShell.QuickActions = function RecordDetailShellQuickActions({
  children,
}: {
  children: ReactNode;
}) {
  return <div className={styles.quickActions}>{children}</div>;
};

type TabsProps = {
  tabs: RecordDetailTab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  ariaLabel: string;
};

RecordDetailShell.Tabs = function RecordDetailShellTabs({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel,
}: TabsProps) {
  return (
    <nav className={styles.tabs} aria-label={ariaLabel}>
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          className={activeTab === t.id ? styles.tabActive : styles.tab}
          aria-current={activeTab === t.id ? 'page' : undefined}
          onClick={() => onTabChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );
};

RecordDetailShell.Body = function RecordDetailShellBody({ children }: { children: ReactNode }) {
  return <div className={styles.body}>{children}</div>;
};

export { styles as recordDetailShellStyles };
